import React, { useState, useMemo } from 'react';
import { FaShieldAlt, FaFileContract, FaCheckCircle, FaExclamationTriangle, FaSearch } from 'react-icons/fa';

// --- Reusable Components ---

// 1. Compliance Risk Card
const ComplianceCard = ({ title, value, status, icon: Icon, colorClass }) => {
  const statusColor = {
    Low: 'text-green-600 bg-green-100',
    Medium: 'text-yellow-600 bg-yellow-100',
    High: 'text-red-600 bg-red-100',
  }[status] || 'text-gray-600 bg-gray-100';

  return (
    <div className={`p-5 rounded-xl shadow-md transition duration-300 hover:shadow-lg bg-white border-l-4 ${colorClass}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-700">{title}</div>
        <Icon className="text-2xl text-gray-500" />
      </div>
      <div className="mt-2 text-3xl font-bold text-gray-900">
        {value}
      </div>
      <div className={`mt-1 text-xs font-bold px-2 py-1 rounded-full w-fit ${statusColor}`}>
        Risk: {status}
      </div>
    </div>
  );
};

// 2. Statutory Filing Tracker Component
const StatutoryFilingTracker = ({ filings }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg">
    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
      <FaFileContract className="mr-3 text-blue-600" /> Mandatory Statutory Filings (India)
    </h2>
    <ul className="space-y-3">
      {filings.map((filing, index) => (
        <li key={index} className="flex justify-between items-center p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition duration-150 rounded-lg">
          <span className="text-gray-700 font-medium flex-1">{filing.name}</span>
          <span className={`text-sm font-semibold w-24 text-center py-1 rounded-full ${filing.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {filing.status}
          </span>
          <span className="text-sm text-gray-500 w-32 text-right">{filing.dueDate}</span>
        </li>
      ))}
    </ul>
  </div>
);

// --- Dummy Data (Indian Context) ---
const mockFilings = [
  { name: 'Monthly PF Return (UAN/ECR)', status: 'Completed', dueDate: 'Sep 15, 2025' },
  { name: 'Monthly ESI Contribution', status: 'Pending', dueDate: 'Oct 15, 2025' },
  { name: 'Annual Form 16 Issuance', status: 'Completed', dueDate: 'Jul 31, 2025' },
  { name: 'Professional Tax (PT) Filing', status: 'Pending', dueDate: 'Oct 20, 2025' },
];

const mockAuditLog = [
    { id: 101, type: 'External Labor Audit', date: 'Aug 20, 2025', findings: 3, status: 'Closed', risk: 'Low' },
    { id: 102, type: 'Internal Payroll Review', date: 'Jul 15, 2025', findings: 7, status: 'In Progress', risk: 'Medium' },
    { id: 103, type: 'POSH Policy Review', date: 'Sep 01, 2025', findings: 0, status: 'Closed', risk: 'Low' },
];

// --- Main Dashboard Component ---

const ComplianceAudit = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAuditLog = useMemo(() => {
    return mockAuditLog.filter(log =>
      log.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.status.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">Compliance & Audit Dashboard 🛡️</h1>
      
      {/* 1. Key Metrics Section - Responsive Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <ComplianceCard
          title="Overall Compliance Status"
          value="95%"
          status="Low"
          icon={FaShieldAlt}
          colorClass="border-green-500"
        />
        <ComplianceCard
          title="Pending Filings (Next 30 Days)"
          value="2"
          status="Medium"
          icon={FaExclamationTriangle}
          colorClass="border-yellow-500"
        />
        <ComplianceCard
          title="Unacknowledged Policies"
          value="14 Employees"
          status="Medium"
          icon={FaFileContract}
          colorClass="border-blue-500"
        />
      </div>

      {/* 2. Statutory Tracker & Policy Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        
        {/* Statutory Tracker (Table/List) */}
        <StatutoryFilingTracker filings={mockFilings} />
        
        {/* Policy Acknowledgment */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaCheckCircle className="mr-3 text-green-600" /> Policy Acknowledgment Summary
            </h2>
            <ul className="space-y-4">
                <li className="p-3 bg-gray-50 rounded-lg shadow-sm">
                    <span className="font-semibold text-gray-700">IT Usage Policy v2.1</span>
                    <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-blue-600">Acknowledged: 86%</span>
                        <button className="text-xs text-indigo-500 hover:text-indigo-700 font-medium">View Pending (14)</button>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: '86%' }}></div>
                    </div>
                </li>
                <li className="p-3 bg-gray-50 rounded-lg shadow-sm">
                    <span className="font-semibold text-gray-700">Anti-Harassment (POSH) Policy</span>
                    <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-green-600">Acknowledged: 100%</span>
                        <span className="text-green-500 font-medium">Complete!</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                </li>
            </ul>
        </div>
      </div>

      {/* 3. Audit Log Table */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Past Audit Log & Findings</h2>
        
        {/* Search Bar */}
        <div className="mb-4 relative">
          <input
            type="text"
            placeholder="Search by Audit Type or Status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Audit Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Findings</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAuditLog.map((log) => (
                <tr key={log.id} className="hover:bg-blue-50 transition duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{log.type}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-500">{log.findings}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      log.status === 'Closed' ? 'bg-green-100 text-green-800' :
                      log.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredAuditLog.length === 0 && (
            <p className="text-center py-4 text-gray-500">No audit records found matching your search.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceAudit;